<template>
  <template v-if="$route.name === 'class-fees.index'">
    <div class="col-12 col-xl-12">
      <div class="container-fluid px-0">
        <div class="row mb-4 g-3 align-items-end">
          <div class="col-6 col-md-3 col-lg-2">
            <select
              v-model="academicYearFilter"
              class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
              @change="fetchClassFees"
            >
              <option value="">{{ t('class_fees.all_years') }}</option>
              <option v-for="year in academicYears" :value="year.id" :key="year.id">
                {{ year.name }} ({{ year.year }})
              </option>
            </select>
          </div>

          <div class="col-6 col-md-3 col-lg-3">
            <select
              v-model="classFilter"
              class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
              @change="fetchClassFees"
            >
              <option value="">{{ t('class_fees.all_classes') }}</option>
              <option v-for="classItem in classes" :value="classItem.id" :key="classItem.id">
                {{ classItem.name }}
              </option>
            </select>
          </div>

          <div class="col-6 col-md-1 d-none d-md-block">
            <button 
              class="btn btn-outline-secondary btn-sm w-100 shadow-xs" 
              @click="resetFilters"
              :title="t('class_fees.reset_filter')"
              :disabled="isLoading"
            >
              <i class="fas fa-filter-circle-xmark"></i>
            </button>
          </div>

          <div class="col-6 d-md-none">
            <button 
              class="btn btn-outline-secondary btn-sm w-100 shadow-xs" 
              @click="resetFilters"
              :disabled="isLoading"
            >
              <i class="fas fa-filter-circle-xmark me-1"></i> {{ t('class_fees.reset') }}
            </button>
          </div>

          <div class="col-6 col-md-5 col-lg-6">
            <router-link 
              :to="{ name: 'class-fees.add' }" 
              class="btn btn-secondary btn-sm w-100 shadow-xs"
              v-can="'class_fees.create'"
            >
              <i class="fas fa-plus me-1"></i>
              {{ t('class_fees.add_fee') }}
            </router-link>
          </div>

          <div class="col-12 col-md-4 col-lg-4">
            <button 
              class="btn btn-primary btn-sm w-100 shadow-xs print-hide" 
              @click="printFeesReport"
              :disabled="classFees.length === 0"
            >
              <i class="fas fa-print me-1"></i>
              {{ t('class_fees.print_fees') }}
            </button>
          </div>
        </div>
      </div>
   
      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3">{{ t('class_fees.fee_record') }}</h6>
            </div>
          </div>
        </div>
        
        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-2 p-md-4">
            <LoadingSpinner v-if="isLoading" :message="t('class_fees.loading_fees')" />
            
            <template v-else>
              <div class="d-none d-xl-block">
                <div class="table-responsive">
                  <table class="table align-items-center mb-0" style="width: 100%">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 25%">{{ t('class_fees.class') }}</th>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 25%">{{ t('class_fees.academic_year') }}</th>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 20%">{{ t('class_fees.amount') }}</th>
                        <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 15%">{{ t('class_fees.actions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="fee in classFees" :key="fee.id">
                        <td>{{ fee.class_name }}</td>
                        <td>
                          <div class="d-flex flex-column">
                            <strong class="text-sm">{{ fee.academic_year_name }}</strong>
                            <small class="text-xs text-muted">{{ fee.academic_year }}</small>
                          </div>
                        </td>
                        <td class="text-center">{{ formatCurrency(fee.amount) }}</td>
                        <td class="align-middle text-center">
                          <div class="d-flex gap-1 justify-content-center">
                            <router-link 
                              :to="{ name: 'class-fees.edit', params: { id: fee.id } }" 
                              class="btn text-primary"
                              :title="t('class_fees.edit')"
                              v-can="'class_fees.update'"
                            >
                              <i class="fas fa-edit"></i>
                            </router-link>
                            <button 
                              class="btn text-danger" 
                              @click="confirmDelete(fee)"
                              :disabled="isDeleting"
                              :title="t('class_fees.delete')"
                              v-can="'class_fees.delete'"
                            >
                              <i v-if="isDeleting && deletingId === fee.id" class="fas fa-spinner fa-spin"></i>
                              <i v-else class="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="classFees.length === 0">
                        <td colspan="4" class="text-center py-4 text-muted">
                          <i class="fas fa-money-bill-wave fa-2x mb-2"></i>
                          <p>{{ t('class_fees.no_fees') }}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div class="d-xl-none">
                <div v-if="classFees.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-money-bill-wave fa-2x mb-2"></i>
                  <p>{{ t('class_fees.no_fees') }}</p>
                </div>
                
                <div v-else class="row g-3">
                  <div class="col-12" v-for="fee in classFees" :key="fee.id">
                    <div class="card shadow-xs border">
                      <div class="card-body">
                        <div class="d-flex justify-content-between mb-2">
                          <span>{{ t('class_fees.class') }}:</span>
                          <strong>{{ fee.class_name }}</strong>
                        </div>
                        
                        <div class="d-flex justify-content-between mb-2">
                          <span>{{ t('class_fees.academic_year') }}:</span>
                          <div class="text-end">
                            <strong class="text-sm">{{ fee.academic_year_name }}</strong>
                            <br>
                            <small class="text-xs text-muted">{{ fee.academic_year }}</small>
                          </div>
                        </div>
                        
                        <div class="d-flex justify-content-between mb-2">
                          <span>{{ t('class_fees.amount') }}:</span>
                          <span>{{ formatCurrency(fee.amount) }}</span>
                        </div>
                        
                        <div class="d-flex justify-content-end gap-2">
                          <router-link 
                            :to="{ name: 'class-fees.edit', params: { id: fee.id } }" 
                            class="btn text-primary"
                            v-can="'class_fees.update'"
                            :title="t('class_fees.edit')"
                          >
                            <i class="fas fa-edit"></i>
                          </router-link>
                          <button 
                            class="btn text-danger" 
                            @click="confirmDelete(fee)"
                            :disabled="isDeleting"
                            v-can="'class_fees.delete'"
                            :title="t('class_fees.delete')"
                          >
                            <i v-if="isDeleting && deletingId === fee.id" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <PaginationControls 
                v-if="classFees.length > 0"
                :pagination="{
                  currentPage: pagination.current_page,
                  totalPages: pagination.last_page,
                  totalItems: pagination.total,
                  from: (pagination.current_page - 1) * pagination.per_page + 1,
                  to: Math.min(pagination.current_page * pagination.per_page, pagination.total)
                }"
                @page-changed="fetchClassFees"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
    <DeleteDialog 
      ref="deleteDialog"
      entity-name="ClassFee"
      :entity-label="t('class_fees.fee')"
      @delete="deleteFee"
    />
  </template>
  
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import DeleteDialog from '@/components/common/DeleteDialog.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t, locale } = useI18n();
const toast = useToast();

const classFees = ref([]);
const classes = ref([]);
const academicYears = ref([]);
const academicYearFilter = ref('');
const classFilter = ref('');
const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0
});
const deleteDialog = ref(null);
const isLoading = ref(false);
const isDeleting = ref(false);
const deletingId = ref(null);

onMounted(async () => {
  // fetchAcademicYears() is what sets academicYearFilter to the active
  // year by default. Firing fetchClassFees() at the same time (instead of
  // after) meant the very first request went out with the filter still
  // empty (all years), even though the dropdown then showed the active
  // year selected a moment later once fetchAcademicYears() resolved.
  // Waiting for it first makes the initial load respect the default filter.
  await Promise.all([fetchAcademicYears(), fetchClasses()]);
  fetchClassFees();
});

const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
    
    // Set default to current active academic year
    const activeYear = academicYears.value.find(year => year.is_active);
    if (activeYear) {
      academicYearFilter.value = activeYear.id;
    }
  } catch (error) {
    notifyError(error, 'errors.load_failed');
  }
};

const fetchClasses = async () => {
  try {
    const response = await api.get('/classes', { params: { all: true } });
    classes.value = response.data.data;
  } catch (error) {
    notifyError(error, 'class_fees.load_classes_failed');
  }
};

const fetchClassFees = async (page = 1) => {
  isLoading.value = true;
  try {
    const params = {
      page,
      per_page: pagination.value.per_page,
      academic_year_id: academicYearFilter.value || undefined,
      class_id: classFilter.value || undefined
    };
    
    const response = await api.get('/payments/class-fees', { params });
    classFees.value = response.data.data;
    pagination.value = {
      current_page: response.data.meta.current_page,
      last_page: response.data.meta.last_page,
      per_page: response.data.meta.per_page,
      total: response.data.meta.total
    };
  } catch (error) {
    notifyError(error, 'class_fees.load_fees_failed');
  } finally {
    isLoading.value = false;
  }
};

function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden'));
  return toast.error(extractApiMessage(error, fallbackKey));
}

const resetFilters = () => {
  const activeYear = academicYears.value.find(year => year.is_active);
  academicYearFilter.value = activeYear ? activeYear.id : '';
  classFilter.value = '';
  fetchClassFees();
};

const confirmDelete = (fee) => {
  deleteDialog.value.openDialog(fee.id, `${t('class_fees.fee')} - ${fee.class_name} (${fee.academic_year_name})`);
};

const deleteFee = async (id) => {
  isDeleting.value = true;
  deletingId.value = id;
  try {
    const resp = await api.delete(`/payments/class-fees/${id}`);
    const status = resp?.status ?? 200;

    if ([200, 204].includes(status)) {
      toast.success(t('class_fees.delete_success'));
      await fetchClassFees(pagination.value.current_page);
    } else {
      toast.error(t('class_fees.delete_failed'));
    }
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden'));
    } else {
      toast.error(extractApiMessage(error, 'class_fees.delete_failed'));
    }
  } finally {
    isDeleting.value = false;
    deletingId.value = null;
  }
};

const printFeesReport = () => {
  const printWindow = window.open('', '_blank');
  
  // Get selected academic year name for the report
  const selectedYear = academicYearFilter.value 
    ? academicYears.value.find(year => year.id === academicYearFilter.value)
    : null;
  
  const printContent = `
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <meta charset="UTF-8">
      <title>${t('class_fees.fees_report_title')}</title>
      <style>
        @page {
          size: A4 landscape;
          margin: 10mm;
        }
        body {
          font-family: 'Arial', sans-serif;
          line-height: 1.6;
          color: #333;
          padding: 10px;
        }
        .print-container {
          max-width: 100%;
          margin: 0 auto;
        }
        .print-header {
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 2px solid #eee;
        }
        .print-title {
          text-align: center;
          margin-bottom: 15px;
          color: #2c3e50;
          font-size: 22px;
          font-weight: bold;
        }
        .print-meta {
          display: flex;
          justify-content: space-between;
          margin-bottom: 15px;
          font-size: 14px;
        }
        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
          font-size: 12px;
        }
        .print-table th {
          background-color: #f8f9fa;
          padding: 8px;
          text-align: center;
          border: 1px solid #dee2e6;
          font-weight: bold;
        }
        .print-table td {
          padding: 6px;
          border: 1px solid #dee2e6;
          text-align: center;
        }
        .print-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .school-header {
          text-align: center;
          margin-bottom: 15px;
        }
        .school-name {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .school-info {
          font-size: 14px;
          color: #666;
        }
        .total-row {
          font-weight: bold;
          background-color: #e9ecef !important;
        }
        .academic-year-info {
          background-color: #e3f2fd;
          padding: 8px;
          border-radius: 4px;
          margin-bottom: 15px;
          text-align: center;
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="school-header">
          <div class="school-info">${t('class_fees.fees_report_title')} - ${t('class_fees.system_name')}</div>
        </div>
        
        <div class="print-title">${t('class_fees.fees_report_title')}</div>
        
        ${selectedYear ? `
          <div class="academic-year-info">
            <strong>${t('class_fees.academic_year')}:</strong> ${selectedYear.name} (${selectedYear.year})
          </div>
        ` : ''}
        
        <div class="print-meta">
          <div>
            <strong>${t('class_fees.report_date')}:</strong> ${new Date().toLocaleDateString('ar-LY')}
          </div>
          <div>
            <strong>${t('class_fees.total_records')}:</strong> ${classFees.value.length}
          </div>
        </div>
        
        <table class="print-table">
          <thead>
            <tr>
              <th>${t('class_fees.class')}</th>
              <th>${t('class_fees.academic_year')}</th>
              <th>${t('class_fees.amount')}</th>
              <th>${t('class_fees.description')}</th>
            </tr>
          </thead>
          <tbody>
            ${classFees.value.map(fee => `
              <tr>
                <td>${fee.class_name}</td>
                <td>${fee.academic_year_name} (${fee.academic_year})</td>
                <td>${formatCurrency(fee.amount)}</td>
                <td>${fee.description || '---'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="margin-top: 20px; text-align: center; font-size: 12px; color: #666;">
          ${t('class_fees.report_generated_on')} ${new Date().toLocaleString('ar-LY')}
        </div>
      </div>
    </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  printWindow.onload = function() {
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
};

const formatCurrency = (amount) => {
  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return "0 " + t('class_fees.lyd_abbr');

  const formatted = numericAmount % 1 === 0
    ? numericAmount.toString()
    : numericAmount.toFixed(2);

  return formatted + " " + t('class_fees.lyd_abbr');
};
</script>